import { MatDialog } from '@angular/material/dialog';
import { Model } from 'survey-core';
import { MediaDialogComponent } from '../media-preview/media-dialog.component';
import { ImagePreviewService } from '../image-preview/image-preview.service';

const WRAPPER_STYLE =
  'display:inline-flex;flex-direction:column;gap:6px;max-width:280px;width:100%;margin:4px 0';
const MEDIA_STYLE =
  'width:100%;max-height:180px;object-fit:contain;border-radius:6px;display:block;background:#f3f4f6';
const BTN_STYLE =
  'align-self:flex-start;padding:3px 10px;border:1px solid #d1d5db;border-radius:4px;background:#fff;cursor:pointer;font-size:12px;color:#374151';

function isVideoFile(file: any): boolean {
  const url: string = typeof file === 'string' ? file : (file?.content ?? '');
  const mime: string = file?.type ?? '';
  return mime.startsWith('video/') || /\.(mp4|mov|webm|ogg|avi)(\?.*)?$/i.test(url);
}

function isImageFile(file: any): boolean {
  const url: string = typeof file === 'string' ? file : (file?.content ?? '');
  const mime: string = file?.type ?? '';
  return mime.startsWith('image/') || /\.(jpe?g|png|gif|webp|svg|bmp|heic)(\?.*)?$/i.test(url);
}

function renderFileQuestion(
  values: any[],
  el: HTMLElement,
  imagePreview: ImagePreviewService,
  dialog: MatDialog
): void {
  const fileRoot = (el.querySelector('.sd-file') as HTMLElement | null) ?? el;
  fileRoot.innerHTML = '';
  fileRoot.style.cssText = 'min-height:0;height:auto;padding:0;border:none;background:none';
  fileRoot.classList.remove('sd-file--single-image');

  values.forEach((file: any) => {
    const url: string = typeof file === 'string' ? file : (file?.content ?? '');
    if (!url) return;

    const wrapper = document.createElement('div');
    wrapper.style.cssText = WRAPPER_STYLE;

    if (isVideoFile(file)) {
      const video = document.createElement('video');
      video.src = url;
      video.controls = true;
      video.style.cssText = MEDIA_STYLE;
      wrapper.appendChild(video);

      const btn = document.createElement('button');
      btn.textContent = 'Full View';
      btn.style.cssText = BTN_STYLE;
      btn.addEventListener('click', () =>
        dialog.open(MediaDialogComponent, {
          data: { mediaPath: url },
          maxWidth: '95vw',
          maxHeight: '95vh',
          width: '95%',
          height: '95%',
        })
      );
      wrapper.appendChild(btn);
    } else if (isImageFile(file)) {
      const img = document.createElement('img');
      img.src = url;
      img.alt = file?.name ?? '';
      img.style.cssText = MEDIA_STYLE + ';cursor:zoom-in';
      img.addEventListener('click', () => imagePreview.open(url, file?.name ?? ''));
      wrapper.appendChild(img);
    } else {
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.textContent = file?.name || url;
      a.style.cssText = 'font-size:13px;color:#2563eb;word-break:break-all';
      wrapper.appendChild(a);
    }

    fileRoot.appendChild(wrapper);
  });
}

/**
 * Attaches unified media render hooks to a SurveyJS model (display mode).
 * All file questions (photo, video, media, attachment) are rendered with the
 * same card layout:
 * - Images → small thumbnail, click opens the image lightbox.
 * - Videos → small inline player + "Full View" button (MediaDialogComponent).
 */
export function attachSurveyMediaHooks(
  model: Model,
  imagePreview: ImagePreviewService,
  dialog: MatDialog
): void {
  const hooked = new WeakSet<HTMLElement>();

  model.onAfterRenderQuestion.add((survey, opts) => {
    if (survey.mode !== 'display') return;
    if (opts.question.getType() !== 'file') return;
    const el = opts.htmlElement as HTMLElement;
    if (hooked.has(el)) return;
    hooked.add(el);

    const values: any[] = Array.isArray(opts.question.value)
      ? opts.question.value
      : opts.question.value
      ? [opts.question.value]
      : [];

    if (!values.length) return;
    renderFileQuestion(values, el, imagePreview, dialog);
  });
}
